import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export interface CustomerListItem {
  visitorId: number;
  lastActionAt: string;
  lastEventType: string;
  lastEventSource: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  landingPage: string | null;
  displayName: string;
  eventCount: number;
  /** "pg" = aktiv im 35d-Window. "bq" = dormant, Last-Action aus BigQuery. */
  source: "pg" | "bq";
}

export interface CustomerListResponse {
  items: CustomerListItem[];
  total: number;
  limit: number;
  offset: number;
  /** True wenn die Antwort dormant Visitor aus BigQuery enthält. */
  bqHasOlder: boolean;
}

export interface EventTypesResponse {
  source: "pg" | "bq";
  items: Array<{ eventType: string; count: number }>;
}

export interface CustomerKey {
  keyType: string;
  value: string;
  originValue: string;
  createdAt: string;
}

export interface CustomerAttribution {
  referrerUrl: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  landingPage: string | null;
  firstSeenAt: string;
}

export interface CustomerLeadDetails {
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  country: string | null;
  industry: string | null;
  lead: unknown;
}

export type EventSource = "pg" | "bq";

/** A server-side CAPI send of this event to one ad platform. */
export interface CapiDelivery {
  /** 'meta' | 'google' | 'linkedin' | … (destination type). */
  type: string;
  /** true = platform accepted; false = send attempted but rejected. */
  ok: boolean;
  /** true = the conversion reached the platform through the twin channel and
   *  this row was deliberately not re-sent (TASK-94). Absent on rows written
   *  before the flag existed — those were all real sends. */
  suppressed?: boolean;
}

export interface CustomerJourneyEvent {
  eventId: string;
  eventType: string;
  eventSource: string | null;
  createdAt: string;
  /** "pg" = aus dem 35-Tage-Hot-Window, "bq" = aus BigQuery nachgeladen. */
  source: EventSource;
  /** Ad-platform CAPI sends for this event (empty for older/bq events). */
  capiDeliveries?: CapiDelivery[];
  /** Shared id of a merged CAPI send — twin events (browser + webhook) of the
   *  same conversion carry the same value; the journey collapses them. */
  capiCanonicalId?: string | null;
  payload: Record<string, unknown>;
}

export interface CustomerDetail {
  visitorId: number;
  projectId: number;
  keys: CustomerKey[];
  attribution: CustomerAttribution | null;
  leadDetails: CustomerLeadDetails | null;
  /** Test-markierter Lead ("Test" im Vor-/Nachnamen): der Worker löst solche
   *  Events bewusst schlüssellos auf, eine leere Keys-Liste ist also erwartet. */
  testLead?: boolean;
  journey: CustomerJourneyEvent[];
  /** Cursor for the BQ "load older" call — oldest createdAt in the PG window. */
  oldestPgEventAt: string | null;
  bqMirrorEnabled: boolean;
  bqDataset: string;
}

/** Backend returns this when the requested visitor was merged into another;
 *  UI should redirect to /customers/{projectId}/{mergedInto}. */
export interface CustomerMergeRedirect {
  mergedInto: number;
}

export type CustomerDetailResponse = CustomerDetail | CustomerMergeRedirect;

export function isMergeRedirect(
  r: CustomerDetailResponse,
): r is CustomerMergeRedirect {
  return "mergedInto" in r && typeof r.mergedInto === "number";
}

export interface BqCountResponse {
  count: number;
  bqMirrorEnabled: boolean;
}

export interface BqEventsPage {
  items: CustomerJourneyEvent[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface ListFilters {
  projectId: number | null;
  eventType?: string;
  q?: string;
  limit?: number;
  offset?: number;
  /** Include bot-flagged visitors. Default false → bots hidden. */
  includeBots?: boolean;
}

export function useCustomers(filters: ListFilters) {
  const enabled = filters.projectId !== null && filters.projectId > 0;
  return useQuery<CustomerListResponse>({
    queryKey: [
      "admin",
      "customers",
      filters.projectId,
      filters.eventType ?? "",
      filters.q ?? "",
      filters.limit ?? 50,
      filters.offset ?? 0,
      filters.includeBots ?? false,
    ],
    queryFn: () => {
      const p = new URLSearchParams();
      if (filters.eventType) p.set("eventType", filters.eventType);
      if (filters.q) p.set("q", filters.q);
      if (filters.limit) p.set("limit", String(filters.limit));
      if (filters.offset) p.set("offset", String(filters.offset));
      if (filters.includeBots) p.set("includeBots", "true");
      const qs = p.toString();
      return api<CustomerListResponse>(
        `/admin/projects/${filters.projectId}/customers${qs ? `?${qs}` : ""}`,
      );
    },
    enabled,
    staleTime: 15_000,
  });
}

export interface EventTypeEntry {
  eventType: string;
  count: number;
}

export function useEventTypes(projectId: number | null) {
  return useQuery<EventTypesResponse>({
    queryKey: ["admin", "event-types", projectId],
    queryFn: () => api(`/admin/projects/${projectId}/event-types`),
    enabled: projectId !== null && projectId > 0,
    staleTime: 60_000,
  });
}

export function useCustomer(projectId: number | null, visitorId: number | null) {
  const enabled =
    projectId !== null && visitorId !== null && visitorId > 0 && projectId > 0;
  return useQuery<CustomerDetailResponse>({
    queryKey: ["admin", "customers", "detail", projectId, visitorId],
    queryFn: () =>
      api<CustomerDetailResponse>(
        `/admin/projects/${projectId}/customers/${visitorId}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * "Wie viele Events liegen in BigQuery, älter als unser Cursor?"
 * Wird beim Page-Load einmal aufgerufen, damit die UI weiß ob ein
 * Load-more-Button überhaupt sinnvoll ist.
 */
export function useCustomerBqCount(
  projectId: number | null,
  visitorId: number | null,
  before: string | null,
) {
  const enabled =
    projectId !== null && visitorId !== null && visitorId > 0 && projectId > 0;
  return useQuery<BqCountResponse>({
    queryKey: ["admin", "customers", "bq-count", projectId, visitorId, before],
    queryFn: () => {
      const qs = before ? `?before=${encodeURIComponent(before)}` : "";
      return api(
        `/admin/projects/${projectId}/customers/${visitorId}/bq-count${qs}`,
      );
    },
    enabled,
    staleTime: 60_000,
  });
}

/** Target: existing visitor by id, or a new visitor minted from email/phone. */
export type AddManualEventTarget =
  | { visitorId: number }
  | { newVisitor: { email?: string; phone?: string } };

export type AddManualEventInput = AddManualEventTarget & {
  eventType: string;
  /** ISO-8601 timestamp of the event. */
  createdAt: string;
  dealValue?: number;
  dealCurrency?: string;
  /** Free-text attribution (TASK-59, S7) — Quelle/Medium/Kampagne. Written
   *  as utm_source/utm_medium/utm_campaign so the touchpoint counts under
   *  the existing origin rule. */
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  /** Ad-set/ad names from the Meta picker (TASK-81) — same treatment as
   *  utmCampaign above, one level down each: utmContent for the ad set,
   *  utmTerm for the ad. Absent when that level wasn't chosen. */
  utmContent?: string;
  utmTerm?: string;
  /** Meta campaign/ad-set/ad ids from the picker (TASK-60, S3) — written
   *  into custom_data as dh_c/dh_as/dh_a by the backend, same shape the
   *  regular pipeline expects. Independent of the UTM fields above; the
   *  picker also sets utmCampaign to the chosen campaign's name. */
  dhC?: string;
  dhAs?: string;
  dhA?: string;
};

/**
 * Manually attach an event to a visitor (existing or newly minted from an
 * email/phone key). The event runs through the normal queue → worker
 * pipeline, so it shows up in the journey after a few seconds — we
 * invalidate immediately and once more shortly after.
 */
export function useAddManualEvent(projectId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddManualEventInput) => {
      const common = {
        eventType: input.eventType,
        createdAt: input.createdAt,
        ...(input.dealValue !== undefined
          ? { dealValue: input.dealValue, dealCurrency: input.dealCurrency }
          : {}),
        ...(input.utmSource !== undefined ? { utmSource: input.utmSource } : {}),
        ...(input.utmMedium !== undefined ? { utmMedium: input.utmMedium } : {}),
        ...(input.utmCampaign !== undefined
          ? { utmCampaign: input.utmCampaign }
          : {}),
        ...(input.utmContent !== undefined
          ? { utmContent: input.utmContent }
          : {}),
        ...(input.utmTerm !== undefined ? { utmTerm: input.utmTerm } : {}),
        ...(input.dhC !== undefined ? { dhC: input.dhC } : {}),
        ...(input.dhAs !== undefined ? { dhAs: input.dhAs } : {}),
        ...(input.dhA !== undefined ? { dhA: input.dhA } : {}),
      };
      if ("visitorId" in input) {
        return api<{ ok: true; event_id: string; visitor_id: number }>(
          `/admin/projects/${projectId}/customers/${input.visitorId}/events`,
          { method: "POST", body: JSON.stringify(common) },
        );
      }
      return api<{ ok: true; event_id: string }>(
        `/admin/projects/${projectId}/events`,
        {
          method: "POST",
          body: JSON.stringify({ ...common, ...input.newVisitor }),
        },
      );
    },
    onSuccess: () => {
      const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["admin", "customers"] });
        qc.invalidateQueries({ queryKey: ["admin", "event-types"] });
      };
      invalidate();
      // Pipeline braucht ein paar Sekunden bis der Event in PG steht.
      setTimeout(invalidate, 5_000);
    },
  });
}

/**
 * Manually merge sourceVisitorId into targetVisitorId ("Merge with" on the
 * customer detail page). Synchronous on the API side — the merged journey is
 * visible right after invalidation.
 */
export function useMergeVisitor(projectId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { targetVisitorId: number; sourceVisitorId: number }) =>
      api<{ ok: true; visitorId: number; mergedVisitorId: number }>(
        `/admin/projects/${projectId}/customers/${input.targetVisitorId}/merge`,
        {
          method: "POST",
          body: JSON.stringify({ sourceVisitorId: input.sourceVisitorId }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "customers"] });
    },
  });
}

export async function fetchCustomerBqPage(
  projectId: number,
  visitorId: number,
  before: string | null,
  limit = 200,
): Promise<BqEventsPage> {
  const p = new URLSearchParams();
  if (before) p.set("before", before);
  p.set("limit", String(limit));
  return api<BqEventsPage>(
    `/admin/projects/${projectId}/customers/${visitorId}/events-bq?${p.toString()}`,
  );
}
